import type { BreachGroupBy, BreachGroupPrefs } from './breachGrouping';

/**
 * Where the out-of-range list's grouping is remembered. Same versioned, all-or-
 * nothing scheme as the shifts grid's `shiftsGroupPrefs`: bump the version rather
 * than widening the parser, because a stored blob missing a field resets every
 * pref, not just the stale one.
 */
export const STORAGE_VERSION = 'v1';
export const BREACH_GROUP_STORAGE_KEY = `doctor.measurements.groupBy.${STORAGE_VERSION}`;

/** Grouped by patient and folded shut — see BreachGroupPrefs on why it stores the open ones. */
export const defaultBreachGroupPrefs: BreachGroupPrefs = {
  groupBy: 'patient',
  selectedGroupIds: [],
  expandedGroupIds: [],
};

const GROUP_BY_VALUES: BreachGroupBy[] = ['patient', 'metric', 'visit', 'none'];

const isStringArray = (v: unknown): v is string[] => Array.isArray(v) && v.every((x) => typeof x === 'string');

export const readBreachGroupPrefs = (): BreachGroupPrefs => {
  try {
    const raw = localStorage.getItem(BREACH_GROUP_STORAGE_KEY);
    if (!raw) return defaultBreachGroupPrefs;
    const parsed = JSON.parse(raw) as unknown;
    if (typeof parsed !== 'object' || parsed === null) return defaultBreachGroupPrefs;
    const { groupBy, selectedGroupIds, expandedGroupIds } = parsed as Record<string, unknown>;
    if (!GROUP_BY_VALUES.includes(groupBy as BreachGroupBy)) return defaultBreachGroupPrefs;
    if (!isStringArray(selectedGroupIds) || !isStringArray(expandedGroupIds)) {
      return defaultBreachGroupPrefs;
    }
    return { groupBy: groupBy as BreachGroupBy, selectedGroupIds, expandedGroupIds };
  } catch {
    // Private mode, cleared site data, or storage denied entirely.
    return defaultBreachGroupPrefs;
  }
};

export const writeBreachGroupPrefs = (prefs: BreachGroupPrefs): void => {
  try {
    localStorage.setItem(BREACH_GROUP_STORAGE_KEY, JSON.stringify(prefs));
  } catch {
    // Preferences are a convenience; failing to persist must never break the page.
  }
};

/**
 * The prefs after a dimension change. The selection and the open groups both name
 * ids of the OLD dimension, and an id is prefixed per dimension so none of them
 * can match under the new one — carrying them over would leave the filter showing
 * tags that select nothing.
 */
export const switchGroupBy = (prefs: BreachGroupPrefs, groupBy: BreachGroupBy): BreachGroupPrefs =>
  prefs.groupBy === groupBy ? prefs : { groupBy, selectedGroupIds: [], expandedGroupIds: [] };

/** One group opened or closed. */
export const toggleGroup = (prefs: BreachGroupPrefs, id: string): BreachGroupPrefs => ({
  ...prefs,
  expandedGroupIds: prefs.expandedGroupIds.includes(id)
    ? prefs.expandedGroupIds.filter((open) => open !== id)
    : [...prefs.expandedGroupIds, id],
});

/**
 * Every group on screen opened.
 *
 * It stores exactly the ids given, replacing what was there, rather than adding
 * to it: the ids of groups the filter is currently hiding have no business being
 * marked open, or clearing the filter later would unfold a screen nobody asked
 * to unfold.
 */
export const expandAll = (prefs: BreachGroupPrefs, ids: string[]): BreachGroupPrefs => ({
  ...prefs,
  expandedGroupIds: [...ids],
});

/** Everything folded shut, which is also how the screen opens. */
export const collapseAll = (prefs: BreachGroupPrefs): BreachGroupPrefs => ({
  ...prefs,
  expandedGroupIds: [],
});
